import { useEffect, useState, useContext } from 'react';
import API from '../api/api';
import { AuthContext } from '../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  MenuItem,
  Switch,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useSnackbar } from '../contexts/SnackbarContext';

const formatCurrency = (value) => {
  if (value == null) return '-';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
};

const getCurrentMonth = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${month}`;
};

export default function Expenses() {
  const { user } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expenses, setExpenses] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    name: '',
    amount: '',
    category: '',
    note: '',
    recurring: false,
    recurring_day_offset: 3,
  });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', amount: '', category: '', note: '' });
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [monthFilterEnabled, setMonthFilterEnabled] = useState(true);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const params = {};
      if (monthFilterEnabled && selectedMonth) params.month = selectedMonth;
      if (selectedUserId !== 'all') params.user_id = selectedUserId;
      const res = await API.get('/expenses', { params });
      setExpenses(res.data || []);
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur lors du chargement des depenses', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await API.get('/expenses/categories');
      setCategories(res.data || []);
      if (res.data.length > 0) {
        setForm((prev) => ({ ...prev, category: prev.category || res.data[0] }));
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur lors du chargement des categories', 'error');
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await API.get('/expenses/templates');
      setTemplates(res.data || []);
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur lors du chargement des modeles', 'error');
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await API.get('/users');
      setUsers(res.data || []);
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur lors du chargement des utilisateurs', 'error');
    }
  };

  useEffect(() => {
    fetchExpenses();
    fetchCategories();
    fetchTemplates();
    fetchUsers();
  }, []);
  useEffect(() => {
    fetchExpenses();
  }, [selectedMonth, selectedUserId, monthFilterEnabled]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await API.post('/expenses', {
        name: form.name,
        amount: Number(form.amount),
        category: form.category,
        note: form.note,
        recurring: form.recurring,
        recurring_day_offset: Number(form.recurring_day_offset),
      });
      setForm((prev) => ({ ...prev, name: '', amount: '', note: '', recurring: false }));
      showSnackbar('Depense enregistree', 'success');
      fetchExpenses();
      fetchTemplates();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur lors de la creation', 'error');
    }
  };

  const startEdit = (exp) => {
    setEditingId(exp.id);
    setEditForm({
      name: exp.name,
      amount: exp.amount,
      category: exp.category,
      note: exp.note || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: '', amount: '', category: '', note: '' });
  };

  const handleEditChange = (e) =>
    setEditForm({ ...editForm, [e.target.name]: e.target.value });

  const handleEditSubmit = async (expId) => {
    try {
      await API.put(`/expenses/${expId}`, {
        name: editForm.name,
        amount: Number(editForm.amount),
        category: editForm.category,
        note: editForm.note,
      });
      showSnackbar('Depense mise a jour', 'success');
      setEditingId(null);
      fetchExpenses();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur lors de la mise a jour', 'error');
    }
  };

  const handlePay = async (expId) => {
    try {
      await API.post(`/expenses/${expId}/pay`);
      showSnackbar('Depense payee', 'success');
      fetchExpenses();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur lors du paiement', 'error');
    }
  };

  const handleTemplateUpdate = async (template) => {
    try {
      await API.put(`/expenses/templates/${template.id}`, {
        amount_default: Number(template.amount_default),
        day_offset: Number(template.day_offset),
        active: template.active !== 0,
      });
      showSnackbar('Modele mis a jour', 'success');
      fetchTemplates();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur lors de la mise a jour du modele', 'error');
    }
  };

  const handleTemplateDelete = async (templateId) => {
    if (!window.confirm('Vorlage wirklich loeschen?')) return;
    try {
      await API.delete(`/expenses/templates/${templateId}`);
      showSnackbar('Modele geloescht', 'success');
      setTemplates((prev) => prev.filter((tpl) => tpl.id !== templateId));
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur beim Loeschen der Vorlage', 'error');
    }
  };

  const filteredExpenses = expenses;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, md: 6 },
        py: 5,
        background: 'var(--app-bg)',
      }}
    >
      <Box
        sx={{
          mb: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            color: 'var(--text-primary)',
            letterSpacing: 1,
            fontSize: { xs: 28, sm: 36, md: 42 },
          }}
        >
          Depenses
        </Typography>
        <Typography sx={{ color: 'var(--text-muted)', mt: 1, maxWidth: 500 }}>
          Suivi des depenses cash par categorie.
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          mt={4}
          justifyContent="center"
        >
          <Chip
            label={`Depenses: ${filteredExpenses.length}`}
            sx={{ bgcolor: '#ef4444', color: 'var(--text-primary)' }}
          />
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          mt={3}
          justifyContent="center"
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={monthFilterEnabled}
                onChange={(e) => setMonthFilterEnabled(e.target.checked)}
                color="primary"
              />
            }
            label="Monat filtern"
            sx={{ color: 'var(--text-primary)' }}
          />
          <TextField
            type="month"
            label="Mois"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            size="small"
            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: { xs: '100%', sm: 180 } }}
            disabled={!monthFilterEnabled}
          />
          <TextField
            select
            label="Employe"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            size="small"
            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: { xs: '100%', sm: 200 } }}
          >
            <MenuItem value="all">Tous</MenuItem>
            {users.map((u) => (
              <MenuItem key={u.id} value={String(u.id)}>
                {u.username}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Box>

      <Paper
        sx={{
          p: 4,
          mb: 6,
          borderRadius: 4,
          background:
            'linear-gradient(135deg, rgba(239,68,68,0.18), rgba(14,165,233,0.1))',
          backdropFilter: 'blur(12px)',
          border: '1px solid var(--border-subtle)',
        }}
      >
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setShowForm((v) => !v)}
            disabled={user?.role !== 'ADMIN'}
            startIcon={<AddCircleOutlineIcon />}
            sx={{
              px: 3,
              borderRadius: 3,
              fontWeight: 600,
              background: 'linear-gradient(90deg, #ef4444, #f97316)',
            }}
          >
            {showForm ? 'Fermer' : 'Ajouter'}
          </Button>
        </Stack>

        {showForm && (
          <Box component="form" onSubmit={handleSubmit}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                name="name"
                label="Nom"
                value={form.name}
                onChange={handleChange}
                required
                fullWidth
                disabled={user?.role !== 'ADMIN'}
                sx={{
                  backgroundColor: 'var(--input-bg)',
                  borderRadius: 2,
                }}
              />
              <TextField
                name="amount"
                label="Montant (EUR)"
                type="number"
                value={form.amount}
                onChange={handleChange}
                required
                fullWidth
                disabled={user?.role !== 'ADMIN'}
                sx={{
                  backgroundColor: 'var(--input-bg)',
                  borderRadius: 2,
                }}
              />
              <TextField
                select
                name="category"
                label="Categorie"
                value={form.category}
                onChange={handleChange}
                required
                fullWidth
                disabled={user?.role !== 'ADMIN'}
                sx={{
                  backgroundColor: 'var(--input-bg)',
                  borderRadius: 2,
                }}
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                name="note"
                label="Note"
                value={form.note}
                onChange={handleChange}
                fullWidth
                disabled={user?.role !== 'ADMIN'}
                sx={{
                  backgroundColor: 'var(--input-bg)',
                  borderRadius: 2,
                }}
              />
              <TextField
                select
                name="recurring"
                label="Recurrent"
                value={form.recurring ? 'yes' : 'no'}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, recurring: e.target.value === 'yes' }))
                }
                fullWidth
                disabled={user?.role !== 'ADMIN'}
                sx={{
                  backgroundColor: 'var(--input-bg)',
                  borderRadius: 2,
                }}
              >
                <MenuItem value="no">Non</MenuItem>
                <MenuItem value="yes">Oui</MenuItem>
              </TextField>
              {form.recurring && (
                <TextField
                  name="recurring_day_offset"
                  label="Jours avant fin du mois"
                  type="number"
                  value={form.recurring_day_offset}
                  onChange={handleChange}
                  fullWidth
                  disabled={user?.role !== 'ADMIN'}
                  sx={{
                    backgroundColor: 'var(--input-bg)',
                    borderRadius: 2,
                  }}
                />
              )}
              <Button
                type="submit"
                variant="contained"
                disabled={user?.role !== 'ADMIN'}
                sx={{
                  px: 4,
                  borderRadius: 3,
                  fontWeight: 600,
                  background: 'linear-gradient(90deg, #ef4444, #f97316)',
                }}
              >
                Enregistrer
              </Button>
            </Stack>
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          {loading ? (
            <Stack alignItems="center">
              <CircularProgress sx={{ color: 'var(--text-primary)' }} />
            </Stack>
          ) : isMobile ? (
            <Stack spacing={2}>
                {filteredExpenses.map((exp) => (
                  <Paper
                  key={exp.id}
                  sx={{
                    p: 2,
                    borderRadius: 4,
                    background:
                      'var(--surface-1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid var(--border-subtle)',
                  }}
                >
                    <Stack spacing={0.6}>
                      <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                        {exp.name} ({exp.category})
                      </Typography>
                      <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                        {formatCurrency(exp.amount)}
                      </Typography>
                      <Typography sx={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                        Employe: {exp.username || '-'}
                      </Typography>
                      <Typography sx={{ color: 'var(--text-muted)', fontSize: 12 }}>
                        {exp.note || '-'}
                      </Typography>
                      <Typography sx={{ color: 'var(--text-dim)', fontSize: 11 }}>
                        {exp.status} · {exp.created_at}
                      </Typography>
                      {exp.status === 'OPEN' && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handlePay(exp.id)}
                          disabled={user?.role !== 'ADMIN'}
                        >
                          Payer
                        </Button>
                      )}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
          ) : (
            <Paper
              sx={{
                borderRadius: 4,
                background:
                  'var(--surface-1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid var(--border-subtle)',
                overflow: 'hidden',
              }}
            >
              <TableContainer sx={{ maxHeight: { xs: 420, md: 560 } }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {['Nom', 'Employe', 'Categorie', 'Montant', 'Statut', 'Note', 'Date', 'Action'].map((label) => (
                        <TableCell
                          key={label}
                          sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 700,
                            backgroundColor: 'var(--table-header-bg)',
                            borderBottom: '1px solid var(--border-subtle)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredExpenses.map((exp) => (
                      <TableRow
                        key={exp.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'var(--surface-3)',
                          },
                        }}
                      >
                        <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                          {editingId === exp.id ? (
                            <TextField
                              name="name"
                              size="small"
                              value={editForm.name}
                              onChange={handleEditChange}
                              sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2 }}
                            />
                          ) : (
                            exp.name
                          )}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {exp.username || '-'}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {editingId === exp.id ? (
                            <TextField
                              name="category"
                              select
                              size="small"
                              value={editForm.category}
                              onChange={handleEditChange}
                              sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2 }}
                            >
                              {categories.map((category) => (
                                <MenuItem key={category} value={category}>
                                  {category}
                                </MenuItem>
                              ))}
                            </TextField>
                          ) : (
                            exp.category
                          )}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {editingId === exp.id ? (
                            <TextField
                              name="amount"
                              size="small"
                              type="number"
                              value={editForm.amount}
                              onChange={handleEditChange}
                              sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, maxWidth: 120 }}
                            />
                          ) : (
                            formatCurrency(exp.amount)
                          )}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {exp.status}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {editingId === exp.id ? (
                            <TextField
                              name="note"
                              size="small"
                              value={editForm.note}
                              onChange={handleEditChange}
                              sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2 }}
                            />
                          ) : (
                            exp.note || '-'
                          )}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {exp.created_at}
                        </TableCell>
                        <TableCell>
                          {exp.status === 'OPEN' ? (
                            editingId === exp.id ? (
                              <Stack direction="row" spacing={1}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleEditSubmit(exp.id)}
                                  disabled={user?.role !== 'ADMIN'}
                                >
                                  Enregistrer
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={cancelEdit}
                                >
                                  Annuler
                                </Button>
                              </Stack>
                            ) : (
                              <Stack direction="row" spacing={1}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => startEdit(exp)}
                                >
                                  Modifier
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handlePay(exp.id)}
                                  disabled={user?.role !== 'ADMIN'}
                                >
                                  Payer
                                </Button>
                              </Stack>
                            )
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      </Paper>

      <Paper
        sx={{
          p: 3,
          mb: 6,
          borderRadius: 4,
          background:
            'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(14,165,233,0.08))',
          backdropFilter: 'blur(12px)',
          border: '1px solid var(--border-subtle)',
        }}
      >
        <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 2 }}>
          Depenses recurrentes (modeles)
        </Typography>
        {templates.length === 0 ? (
          <Typography sx={{ color: 'var(--text-muted)' }}>
            Aucun modele actif.
          </Typography>
        ) : isMobile ? (
          <Stack spacing={2}>
            {templates.map((tpl, index) => (
              <Paper
                key={tpl.id}
                sx={{
                  p: 2,
                  borderRadius: 4,
                  background: 'var(--surface-1)',
                  border: '1px solid var(--border-subtle)',
                }}
              >
                <Stack spacing={1.2}>
                  <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                    {tpl.name}
                  </Typography>
                  <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    {tpl.category}
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    label="Montant"
                    value={tpl.amount_default}
                    onChange={(e) => {
                      const value = e.target.value;
                      setTemplates((prev) =>
                        prev.map((row, i) =>
                          i === index ? { ...row, amount_default: value } : row
                        )
                      );
                    }}
                    sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Jours avant fin"
                    value={tpl.day_offset}
                    onChange={(e) => {
                      const value = e.target.value;
                      setTemplates((prev) =>
                        prev.map((row, i) =>
                          i === index ? { ...row, day_offset: value } : row
                        )
                      );
                    }}
                    sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2 }}
                  />
                  <TextField
                    select
                    size="small"
                    label="Actif"
                    value={tpl.active ? 'yes' : 'no'}
                    onChange={(e) => {
                      const value = e.target.value === 'yes';
                      setTemplates((prev) =>
                        prev.map((row, i) =>
                          i === index ? { ...row, active: value } : row
                        )
                      );
                    }}
                    sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2 }}
                  >
                    <MenuItem value="yes">Oui</MenuItem>
                    <MenuItem value="no">Non</MenuItem>
                  </TextField>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleTemplateUpdate(tpl)}
                      disabled={user?.role !== 'ADMIN'}
                    >
                      Enregistrer
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => handleTemplateDelete(tpl.id)}
                      disabled={user?.role !== 'ADMIN'}
                    >
                      Supprimer
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Nom', 'Categorie', 'Montant', 'Jours avant fin', 'Actif', 'Enregistrer', 'Supprimer'].map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        color: 'var(--text-primary)',
                        fontWeight: 700,
                        backgroundColor: 'var(--table-header-bg)',
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((tpl, index) => (
                  <TableRow key={tpl.id}>
                    <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>{tpl.name}</TableCell>
                    <TableCell sx={{ color: 'var(--text-secondary)' }}>{tpl.category}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={tpl.amount_default}
                        onChange={(e) => {
                          const value = e.target.value;
                          setTemplates((prev) =>
                            prev.map((row, i) =>
                              i === index ? { ...row, amount_default: value } : row
                            )
                          );
                        }}
                        sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, maxWidth: 120 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={tpl.day_offset}
                        onChange={(e) => {
                          const value = e.target.value;
                          setTemplates((prev) =>
                            prev.map((row, i) =>
                              i === index ? { ...row, day_offset: value } : row
                            )
                          );
                        }}
                        sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, maxWidth: 120 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        value={tpl.active ? 'yes' : 'no'}
                        onChange={(e) => {
                          const value = e.target.value === 'yes';
                          setTemplates((prev) =>
                            prev.map((row, i) =>
                              i === index ? { ...row, active: value } : row
                            )
                          );
                        }}
                        sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: 110 }}
                      >
                        <MenuItem value="yes">Oui</MenuItem>
                        <MenuItem value="no">Non</MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleTemplateUpdate(tpl)}
                        disabled={user?.role !== 'ADMIN'}
                      >
                        Enregistrer
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleTemplateDelete(tpl.id)}
                        disabled={user?.role !== 'ADMIN'}
                      >
                        Supprimer
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}




